import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { DonutCard, BarCard, COLORS } from "../../components/charts/DashboardCharts";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
API.interceptors.request.use((c) => { const t = localStorage.getItem("abs_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const rp = (n) => (n == null ? "-" : `Rp ${Number(n).toLocaleString("id-ID")}`);
const badge = (s) => ({
  UNREAD: "bg-blue-500", READ: "bg-slate-400",
  ACKNOWLEDGED: "bg-emerald-500", DISMISSED: "bg-slate-500",
  PENDING: "bg-amber-500", APPROVED: "bg-emerald-500",
  REJECTED: "bg-red-500", OPEN: "bg-blue-500",
}[s] || "bg-slate-500");


// =========================================================
// HRD DASHBOARD
// =========================================================
export function HRDDashboardPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { API.get("/hr-8h/hrd-dashboard").then((r) => setD(r.data)).catch((e) => { const m = e.response?.data?.detail || "Gagal memuat"; setErr(m); toast.error(m); }); }, []);
  if (err) return <div className="p-6" data-testid="hrd-error"><Card><CardContent className="pt-6"><h2 className="text-xl font-bold text-red-600">Akses Ditolak</h2><p>{err}</p></CardContent></Card></div>;
  if (!d) return <div className="p-6" data-testid="hrd-loading">Memuat…</div>;
  const stat = (k, v, cls = "") => (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{k}</CardTitle></CardHeader>
      <CardContent className={`text-2xl font-bold ${cls}`} data-testid={`stat-${k.replace(/\s/g, "-").toLowerCase()}`}>{v ?? "-"}</CardContent></Card>
  );
  const ab = d.approval_pending_breakdown || {};
  // "Lainnya" bucket to make donut composition reach total_karyawan if available
  const lainnya = Math.max(0, (d.total_karyawan || 0)
    - (d.karyawan_aktif || 0)
    - (d.probation_akan_berakhir || 0)
    - (d.kontrak_akan_berakhir || 0)
    - (d.offboarding_pending || 0));
  return (
    <div className="p-6 space-y-4" data-testid="hrd-dashboard">
      <h1 className="text-3xl font-bold">Dashboard HRD — Tahap 8H</h1>
      <div className="grid md:grid-cols-4 gap-3">
        {stat("Total Karyawan", d.total_karyawan)}
        {stat("Karyawan Aktif", d.karyawan_aktif, "text-emerald-600")}
        {stat("Karyawan Baru Bulan Ini", d.karyawan_baru_bulan_ini, "text-blue-600")}
        {stat("Offboarding Bulan Ini", d.offboarding_bulan_ini)}
        {stat("Kontrak Akan Berakhir", d.kontrak_akan_berakhir, "text-amber-600")}
        {stat("Probation Akan Berakhir", d.probation_akan_berakhir, "text-amber-600")}
        {stat("Kehadiran Hari Ini", d.kehadiran_hari_ini)}
        {stat("Terlambat", d.terlambat, "text-red-500")}
        {stat("Tidak Hadir", d.tidak_hadir, "text-red-600")}
        {stat("Cuti Hari Ini", d.cuti_hari_ini)}
        {stat("Lembur Pending", d.lembur_pending)}
        {stat("Klaim Pending", d.klaim_pending, "text-amber-600")}
        {stat("Rekrutmen Aktif", d.rekrutmen_aktif)}
        {stat("Onboarding Pending", d.onboarding_pending)}
        {stat("Offboarding Pending", d.offboarding_pending)}
        {stat("Approval Pending", d.approval_pending_total, "text-red-600")}
      </div>

      {/* Phase 1 charts — recharts, real HRD data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard
          title="Komposisi Status Karyawan"
          testId="chart-hrd-composition"
          hint="Belum ada data karyawan."
          data={[
            { name: "Aktif",              value: d.karyawan_aktif          || 0, color: COLORS.positive },
            { name: "Probation Berakhir", value: d.probation_akan_berakhir || 0, color: COLORS.warning },
            { name: "Kontrak Berakhir",   value: d.kontrak_akan_berakhir   || 0, color: COLORS.warning },
            { name: "Offboarding",        value: d.offboarding_pending     || 0, color: COLORS.risk },
            { name: "Lainnya",            value: lainnya,                        color: COLORS.muted },
          ]}
        />
        <BarCard
          title="Approval Pending per Jenis"
          testId="chart-hrd-approval"
          hint="Tidak ada approval pending."
          data={[
            { name: "Cuti",              value: ab.leave                 || 0 },
            { name: "Lembur",            value: ab.overtime              || 0 },
            { name: "Koreksi Kehadiran", value: ab.attendance_correction || 0 },
            { name: "Klaim",             value: ab.claim                 || 0 },
          ]}
          bars={[{ key: "value", label: "Pending", color: COLORS.warning }]}
        />
      </div>

      {d.payroll_last_period && (
        <Card><CardHeader><CardTitle>Payroll Periode Terakhir</CardTitle></CardHeader>
          <CardContent>Tahun {d.payroll_last_period.year} — Bulan {d.payroll_last_period.month} — Status <Badge>{d.payroll_last_period.status}</Badge></CardContent></Card>
      )}
      {d.approval_pending_breakdown && (
        <Card><CardHeader><CardTitle>Rincian Approval Pending</CardTitle></CardHeader><CardContent>
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="text-xs">Cuti</Label><div className="font-bold">{d.approval_pending_breakdown.leave}</div></div>
            <div><Label className="text-xs">Lembur</Label><div className="font-bold">{d.approval_pending_breakdown.overtime}</div></div>
            <div><Label className="text-xs">Koreksi Kehadiran</Label><div className="font-bold">{d.approval_pending_breakdown.attendance_correction}</div></div>
            <div><Label className="text-xs">Klaim</Label><div className="font-bold">{d.approval_pending_breakdown.claim}</div></div>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}


// =========================================================
// ADMIN DASHBOARD
// =========================================================
export function AdminDashboardPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { API.get("/hr-8h/admin-dashboard").then((r) => setD(r.data)).catch((e) => { const m = e.response?.data?.detail || "Gagal memuat"; setErr(m); toast.error(m); }); }, []);
  if (err) return <div className="p-6" data-testid="admin-error"><Card><CardContent className="pt-6"><h2 className="text-xl font-bold text-red-600">Akses Ditolak</h2><p>{err}</p></CardContent></Card></div>;
  if (!d) return <div className="p-6" data-testid="admin-loading">Memuat…</div>;
  const stat = (k, v, cls = "") => (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{k}</CardTitle></CardHeader>
      <CardContent className={`text-2xl font-bold ${cls}`}>{v ?? "-"}</CardContent></Card>
  );
  return (
    <div className="p-6 space-y-4" data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold">Dashboard Admin — Tahap 8H</h1>
      <div className="grid md:grid-cols-4 gap-3">
        {stat("Pending Approval", d.pending_approval, "text-amber-600")}
        {stat("Dokumen Expiring (30h)", d.dokumen_expiring)}
        {stat("Kontrak Expiring (30h)", d.kontrak_expiring)}
        {stat("Koreksi Kehadiran", d.attendance_correction_pending)}
        {stat("Klaim Pending", d.claim_pending)}
        {stat("Vacancy Open", d.recruitment_vacancy_open)}
        {stat("Kandidat Baru (7h)", d.recruitment_new_apps_7d)}
        {stat("Onboarding Overdue", d.onboarding_task_overdue, "text-red-600")}
        {stat("Clearance Pending", d.offboarding_clearance_pending)}
        {stat("Notifikasi Belum Dibaca", d.notification_unread, "text-blue-600")}
      </div>

      {/* Phase 1 charts — recharts, real Admin data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard
          title="Risiko Expiring & Overdue"
          testId="chart-admin-risk"
          hint="Tidak ada item expiring atau overdue."
          data={[
            { name: "Dokumen Expiring (30h)", value: d.dokumen_expiring              || 0, color: COLORS.warning },
            { name: "Kontrak Expiring (30h)", value: d.kontrak_expiring              || 0, color: COLORS.warning },
            { name: "Onboarding Overdue",     value: d.onboarding_task_overdue       || 0, color: COLORS.risk },
            { name: "Clearance Pending",      value: d.offboarding_clearance_pending || 0, color: COLORS.muted },
          ]}
        />
        <BarCard
          title="Rekrutmen & Approval"
          testId="chart-admin-recruitment"
          hint="Belum ada aktivitas rekrutmen / approval."
          data={[
            { name: "Vacancy Open",        value: d.recruitment_vacancy_open || 0 },
            { name: "Kandidat Baru (7h)",  value: d.recruitment_new_apps_7d  || 0 },
            { name: "Pending Approval",    value: d.pending_approval         || 0 },
            { name: "Klaim Pending",       value: d.claim_pending            || 0 },
          ]}
          bars={[{ key: "value", label: "Jumlah", color: COLORS.baseline }]}
        />
      </div>

      {d.payroll_last_period ? (
        <Card><CardHeader><CardTitle>Payroll Periode Terakhir</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-4 gap-3">
            <div><Label className="text-xs">Periode</Label><div>{d.payroll_last_period.year}/{d.payroll_last_period.month}</div></div>
            <div><Label className="text-xs">Status</Label><Badge>{d.payroll_last_period.status}</Badge></div>
            <div><Label className="text-xs">Total Gross</Label><div className="font-bold">{rp(d.payroll_last_period.total_gross)}</div></div>
            <div><Label className="text-xs">Total Net</Label><div className="font-bold text-emerald-600">{rp(d.payroll_last_period.total_net)}</div></div>
          </CardContent></Card>
      ) : (
        <Card><CardContent className="pt-4 text-slate-500 italic">Nominal payroll tidak ditampilkan (butuh izin khusus).</CardContent></Card>
      )}
    </div>
  );
}


// =========================================================
// ESS
// =========================================================
export function ESSHomePage() {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { API.get("/hr-8h/ess/me").then((r) => setMe(r.data)).catch((e) => { const m = e.response?.data?.detail || "Anda belum terhubung karyawan"; setErr(m); toast.error(m); }); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="ess-home">
      <h1 className="text-3xl font-bold">Portal Karyawan (ESS)</h1>
      {err && <Card><CardContent className="pt-6"><p className="text-amber-600 italic">{err}</p></CardContent></Card>}
      {me && <Card><CardContent className="grid grid-cols-3 gap-3 pt-4">
        <div><Label className="text-xs">Nama</Label><div className="font-bold">{me.full_name}</div></div>
        <div><Label className="text-xs">Kode</Label><div>{me.code}</div></div>
        <div><Label className="text-xs">Departemen</Label><div>{me.department || "-"}</div></div>
        <div><Label className="text-xs">Posisi</Label><div>{me.position || "-"}</div></div>
        <div><Label className="text-xs">Email Kantor</Label><div>{me.work_email}</div></div>
        <div><Label className="text-xs">Tgl Bergabung</Label><div>{me.join_date}</div></div>
        <div><Label className="text-xs">NIK</Label><div className="font-mono">{me.nik_ktp || "-"}</div></div>
        <div><Label className="text-xs">NPWP</Label><div className="font-mono">{me.npwp || "-"}</div></div>
        <div><Label className="text-xs">Rekening</Label><div className="font-mono">{me.bank_account_number || "-"}</div></div>
      </CardContent></Card>}
      <div className="grid md:grid-cols-4 gap-3">
        <a href="/ess/kehadiran"><Card className="hover:shadow-md" data-testid="link-ess-att"><CardHeader><CardTitle>Kehadiran Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/cuti"><Card className="hover:shadow-md" data-testid="link-ess-leave"><CardHeader><CardTitle>Cuti Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/lembur"><Card className="hover:shadow-md" data-testid="link-ess-ot"><CardHeader><CardTitle>Lembur Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/klaim"><Card className="hover:shadow-md" data-testid="link-ess-claim"><CardHeader><CardTitle>Klaim Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/payslip"><Card className="hover:shadow-md" data-testid="link-ess-payslip"><CardHeader><CardTitle>Payslip Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/pinjaman"><Card className="hover:shadow-md" data-testid="link-ess-loan"><CardHeader><CardTitle>Pinjaman/Kasbon</CardTitle></CardHeader></Card></a>
        <a href="/ess/dokumen"><Card className="hover:shadow-md" data-testid="link-ess-doc"><CardHeader><CardTitle>Dokumen Saya</CardTitle></CardHeader></Card></a>
        <a href="/ess/onboarding"><Card className="hover:shadow-md" data-testid="link-ess-onb"><CardHeader><CardTitle>Task Onboarding</CardTitle></CardHeader></Card></a>
      </div>
    </div>
  );
}


export function ESSSimpleListPage({ title, endpoint, testid, cols = [] }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { API.get(endpoint).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <div className="p-6 space-y-4" data-testid={testid}>
      <h1 className="text-2xl font-bold">{title}</h1>
      <Card><CardContent className="pt-4"><Table>
        <TableHeader><TableRow>{cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.map((r, i) => (
          <TableRow key={r.id || i}>{cols.map((c) => <TableCell key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "-")}</TableCell>)}</TableRow>
        ))}</TableBody>
      </Table></CardContent></Card>
    </div>
  );
}
export const ESSAttendancePage = () => <ESSSimpleListPage title="Kehadiran Saya" endpoint="/hr-8h/ess/attendance" testid="ess-attendance" cols={[
  { key: "date", label: "Tanggal" }, { key: "clock_in", label: "Masuk" },
  { key: "clock_out", label: "Pulang" }, { key: "late_minutes", label: "Terlambat (menit)" },
  { key: "status", label: "Status" },
]} />;
export const ESSLeavePage = () => <ESSSimpleListPage title="Cuti Saya" endpoint="/hr-8h/ess/leave" testid="ess-leave" cols={[
  { key: "number", label: "No" }, { key: "leave_type", label: "Jenis" },
  { key: "start_date", label: "Mulai" }, { key: "end_date", label: "Selesai" },
  { key: "status", label: "Status", render: (r) => <Badge className={badge(r.status)}>{r.status}</Badge> },
]} />;
export const ESSOvertimePage = () => <ESSSimpleListPage title="Lembur Saya" endpoint="/hr-8h/ess/overtime" testid="ess-overtime" cols={[
  { key: "number", label: "No" }, { key: "date", label: "Tanggal" },
  { key: "total_hours", label: "Jam" },
  { key: "status", label: "Status", render: (r) => <Badge className={badge(r.status)}>{r.status}</Badge> },
]} />;
export const ESSClaimPage = () => <ESSSimpleListPage title="Klaim Saya" endpoint="/hr-8h/ess/claim" testid="ess-claim" cols={[
  { key: "number", label: "No" }, { key: "category", label: "Kategori" },
  { key: "amount", label: "Nominal", render: (r) => rp(r.amount) },
  { key: "status", label: "Status", render: (r) => <Badge className={badge(r.status)}>{r.status}</Badge> },
]} />;
export const ESSPayslipPage = () => <ESSSimpleListPage title="Payslip Saya" endpoint="/hr-8h/ess/payslip" testid="ess-payslip" cols={[
  { key: "period_year", label: "Tahun" }, { key: "period_month", label: "Bulan" },
  { key: "gross", label: "Gross", render: (r) => rp(r.gross) },
  { key: "total_deduction", label: "Potongan", render: (r) => rp(r.total_deduction) },
  { key: "net", label: "Take Home", render: (r) => rp(r.net) },
]} />;
export const ESSLoanPage = () => <ESSSimpleListPage title="Pinjaman/Kasbon" endpoint="/hr-8h/ess/loans" testid="ess-loans" cols={[
  { key: "number", label: "No" }, { key: "kind", label: "Jenis" },
  { key: "amount", label: "Total", render: (r) => rp(r.amount) },
  { key: "outstanding", label: "Sisa", render: (r) => rp(r.outstanding) },
  { key: "status", label: "Status" },
]} />;
export const ESSDocumentsPage = () => <ESSSimpleListPage title="Dokumen Saya" endpoint={`/hr-8h/documents?employee_id=${localStorage.getItem("abs_linked_emp") || ""}`} testid="ess-docs" cols={[
  { key: "kind", label: "Jenis" }, { key: "title", label: "Judul" },
  { key: "expiry_date", label: "Berakhir" },
]} />;
export function ESSOnboardingTasksPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { API.get("/hr-8h/ess/onboarding-tasks").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="ess-onboarding-tasks">
      <h1 className="text-2xl font-bold">Task Onboarding Saya</h1>
      {rows.length === 0 && <p className="italic text-slate-500">Tidak ada onboarding aktif.</p>}
      {rows.map((ob) => (
        <Card key={ob.id}><CardHeader><CardTitle>{ob.number} — {ob.progress_pct}%</CardTitle></CardHeader>
          <CardContent><Table><TableHeader><TableRow>
            <TableHead>Task</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>{(ob.tasks || []).map((t) => (
            <TableRow key={t.code}><TableCell>{t.name}</TableCell><TableCell>{t.due_date || "-"}</TableCell>
              <TableCell><Badge>{t.status}</Badge></TableCell></TableRow>
          ))}</TableBody></Table></CardContent></Card>
      ))}
    </div>
  );
}


// =========================================================
// MSS
// =========================================================
export function MSSPage() {
  const [tab, setTab] = useState("team");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const ep = { team: "/hr-8h/mss/team", attendance: "/hr-8h/mss/attendance",
                 leave: "/hr-8h/mss/leave", overtime: "/hr-8h/mss/overtime",
                 claim: "/hr-8h/mss/claim" }[tab];
    API.get(ep).then((r) => setRows(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal"));
  }, [tab]);
  return (
    <div className="p-6 space-y-4" data-testid="mss-page">
      <h1 className="text-3xl font-bold">Manager Self-Service — Team Scope</h1>
      <div className="space-x-2">
        {["team", "attendance", "leave", "overtime", "claim"].map((t) => (
          <Button key={t} variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} data-testid={`mss-tab-${t}`}>{t}</Button>
        ))}
      </div>
      <Card><CardContent className="pt-4"><pre className="text-xs bg-slate-50 p-3 rounded max-h-96 overflow-auto">{JSON.stringify(rows, null, 2)}</pre></CardContent></Card>
      {rows.length === 0 && <p className="italic text-slate-500">Tidak ada bawahan / data.</p>}
    </div>
  );
}


// =========================================================
// APPROVAL CENTER
// =========================================================
export function ApprovalCenterPage() {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const reload = () => API.get(`/hr-8h/approval-center${kind ? `?kind=${kind}` : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [kind]);
  return (
    <div className="p-6 space-y-4" data-testid="approval-center-page">
      <h1 className="text-3xl font-bold">Approval Center</h1>
      <div className="space-x-2">
        <Button variant={!kind ? "default" : "outline"} onClick={() => setKind("")}>Semua</Button>
        {["leave", "overtime", "attendance_correction", "claim", "mpr", "offer", "onboarding", "offboarding"].map((k) => (
          <Button key={k} variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)} data-testid={`ac-tab-${k}`}>{k}</Button>
        ))}
      </div>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Jenis</TableHead><TableHead>No</TableHead><TableHead>Requester</TableHead>
        <TableHead>Tanggal</TableHead><TableHead>Nominal</TableHead><TableHead>Umur</TableHead>
        <TableHead>Status</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r, i) => (
        <TableRow key={i} data-testid={`ac-row-${i}`}>
          <TableCell><Badge>{r.kind}</Badge></TableCell>
          <TableCell>{r.number}</TableCell>
          <TableCell>{r.employee_name || r.candidate_name || "-"}</TableCell>
          <TableCell>{r.start_date || r.date || r.created_at?.slice(0, 10)}</TableCell>
          <TableCell>{r.amount != null ? rp(r.amount) : (r.total_hours ? `${r.total_hours} jam` : "-")}</TableCell>
          <TableCell>{r.age_days} hari</TableCell>
          <TableCell><Badge className={badge(r.status)}>{r.status}</Badge></TableCell>
        </TableRow>
      ))}</TableBody></Table>
      {rows.length === 0 && <p className="italic text-slate-500 mt-4">Tidak ada approval pending.</p>}
      </CardContent></Card>
    </div>
  );
}


// =========================================================
// NOTIFICATION CENTER
// =========================================================
export function NotificationCenterPage() {
  const [rows, setRows] = useState([]);
  const reload = () => API.get("/hr-8h/notifications").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const setStatus = async (nid, status) => {
    try { await API.post(`/hr-8h/notifications/${nid}/status`, { status }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="notification-center-page">
      <h1 className="text-3xl font-bold">Notification Center</h1>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Judul</TableHead><TableHead>Kind</TableHead><TableHead>Pesan</TableHead>
        <TableHead>Status</TableHead><TableHead>Aksi</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((n) => (
        <TableRow key={n.id} data-testid={`notif-row-${n.id.slice(0, 8)}`}>
          <TableCell><b>{n.title}</b></TableCell>
          <TableCell><Badge>{n.kind}</Badge></TableCell>
          <TableCell className="text-sm">{n.message}</TableCell>
          <TableCell><Badge className={badge(n.status)}>{n.status}</Badge></TableCell>
          <TableCell className="space-x-1">
            {n.status === "UNREAD" && <Button size="sm" onClick={() => setStatus(n.id, "READ")}>Read</Button>}
            {(n.status === "UNREAD" || n.status === "READ") && <Button size="sm" variant="outline" onClick={() => setStatus(n.id, "ACKNOWLEDGED")}>Acknowledge</Button>}
            {n.status !== "DISMISSED" && <Button size="sm" variant="destructive" onClick={() => setStatus(n.id, "DISMISSED")}>Dismiss</Button>}
          </TableCell>
        </TableRow>
      ))}</TableBody></Table>
      {rows.length === 0 && <p className="italic text-slate-500 mt-4">Tidak ada notifikasi.</p>}
      </CardContent></Card>
    </div>
  );
}


// =========================================================
// PASSWORD RESET (admin issue + confirm)
// =========================================================
export function PasswordResetAdminPage() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [reason, setReason] = useState("");
  const [issued, setIssued] = useState(null);
  useEffect(() => { API.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const issue = async () => {
    setIssued(null);
    try {
      const r = await API.post("/hr-8h/password-reset/request", { user_id: uid, reason });
      setIssued(r.data);
      toast.success("Token diterbitkan. Tampilkan sekali ke karyawan.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="pwd-reset-admin-page">
      <h1 className="text-3xl font-bold">Terbitkan Token Reset Password</h1>
      <Card><CardContent className="pt-4 grid grid-cols-3 gap-3">
        <div><Label>Target User</Label>
          <select className="border rounded p-2 w-full" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="pwd-user">
            <option value="">Pilih…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.email} ({u.role})</option>)}
          </select>
        </div>
        <div className="col-span-2"><Label>Alasan (opsional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pwd-reason" /></div>
        <Button onClick={issue} disabled={!uid} data-testid="pwd-issue">Terbitkan Token</Button>
      </CardContent></Card>
      {issued && (
        <Card className="border-emerald-400 border-2"><CardHeader><CardTitle className="text-emerald-700">Token Diterbitkan — Salin sekarang</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div><Label className="text-xs">Token</Label><div className="font-mono bg-slate-900 text-emerald-300 p-3 rounded break-all" data-testid="pwd-token">{issued.token}</div></div>
            <div><Label className="text-xs">Berlaku hingga</Label><div>{issued.expires_at}</div></div>
            <div className="text-sm italic text-slate-500">Token tidak dapat ditampilkan ulang. Sampaikan secara aman ke karyawan.</div>
          </CardContent></Card>
      )}
    </div>
  );
}


export function PasswordResetConfirmPage() {
  const params = new URLSearchParams(window.location.search);
  const [token, setToken] = useState(params.get("token") || "");
  const [pwd, setPwd] = useState("");
  const [done, setDone] = useState(false);
  const submit = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hr-8h/password-reset/confirm`,
        { token, new_password: pwd });
      setDone(true);
      toast.success("Password diperbarui. Silakan login.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="pwd-reset-confirm-page">
      <Card className="w-full max-w-md">
        <CardHeader><CardTitle>Reset Kata Sandi</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Token</Label><Input value={token} onChange={(e) => setToken(e.target.value)} data-testid="pwd-confirm-token" /></div>
          <div><Label>Kata Sandi Baru</Label><Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="pwd-confirm-pwd" /></div>
          <Button onClick={submit} disabled={!token || pwd.length < 8} data-testid="pwd-confirm-submit">Simpan Password</Button>
          {done && <div className="text-emerald-600">Berhasil. <a href="/login" className="underline">Klik untuk login.</a></div>}
        </CardContent>
      </Card>
    </div>
  );
}


// =========================================================
// ENCRYPTED FIELD EDITOR (reusable "Ubah Data" pattern)
// =========================================================
export function EncryptedFieldEditor({ employeeId, field, label, currentMasked, canEdit = true, canReveal = false }) {
  const [editing, setEditing] = useState(false);
  const [revealValue, setRevealValue] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const save = async () => {
    if (newValue.includes("*")) {
      toast.error("Nilai masih ter-mask. Silakan input nilai penuh.");
      return;
    }
    try {
      await API.post(`/hr-8h/employees/${employeeId}/encrypted-field/edit`,
        { field, new_value: newValue, reason });
      toast.success("Data diperbarui");
      setEditing(false); setNewValue(""); setReason("");
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const doReveal = async () => {
    if (reason.length < 5) { toast.error("Alasan reveal wajib min 5 karakter"); return; }
    try {
      const r = await API.post(`/hr-8h/employees/${employeeId}/encrypted-field/reveal`,
        { field, reason });
      setRevealValue(r.data.value);
      toast.success("Value ditampilkan (audit-logged)");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="border rounded p-3 space-y-2" data-testid={`enc-field-${field}`}>
      <Label className="text-xs">{label}</Label>
      <div className="font-mono">{revealValue || currentMasked || "-"}</div>
      {!editing && (
        <div className="space-x-2">
          {canEdit && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`enc-edit-${field}`}>Ubah Data</Button>}
          {canReveal && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`enc-reveal-${field}`}>Reveal (audit)</Button>}
        </div>
      )}
      {editing && (
        <div className="space-y-2 border-t pt-2">
          {canEdit && <>
            <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Nilai baru (bukan masked)" data-testid={`enc-new-${field}`} />
          </>}
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan (audit)" data-testid={`enc-reason-${field}`} />
          <div className="space-x-2">
            {canEdit && <Button size="sm" onClick={save} data-testid={`enc-save-${field}`}>Simpan</Button>}
            {canReveal && <Button size="sm" variant="secondary" onClick={doReveal} data-testid={`enc-do-reveal-${field}`}>Reveal Value</Button>}
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setNewValue(""); setReason(""); }}>Batal</Button>
          </div>
        </div>
      )}
    </div>
  );
}


// =========================================================
// DOCUMENT CENTER
// =========================================================
export function DocumentCenterPage() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ kind: "", expiring_days: "" });
  const reload = () => {
    const p = new URLSearchParams();
    if (filter.kind) p.append("kind", filter.kind);
    if (filter.expiring_days) p.append("expiring_days", filter.expiring_days);
    API.get(`/hr-8h/documents?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, [filter]);
  return (
    <div className="p-6 space-y-4" data-testid="document-center-page">
      <h1 className="text-3xl font-bold">HR Document Center</h1>
      <Card><CardContent className="pt-4 grid grid-cols-3 gap-3">
        <div><Label>Jenis</Label><Input value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })} placeholder="KONTRAK/KTP/..." /></div>
        <div><Label>Expiring dalam (hari)</Label><Input type="number" value={filter.expiring_days} onChange={(e) => setFilter({ ...filter, expiring_days: e.target.value })} /></div>
      </CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Karyawan</TableHead><TableHead>Jenis</TableHead><TableHead>Judul</TableHead>
        <TableHead>Berakhir</TableHead><TableHead>Aksi</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((d) => (
        <TableRow key={d.id} data-testid={`doc-row-${d.id.slice(0, 8)}`}>
          <TableCell>{d.employee_id || "-"}</TableCell>
          <TableCell><Badge>{d.kind}</Badge></TableCell>
          <TableCell>{d.title}</TableCell>
          <TableCell>{d.expiry_date || "-"}</TableCell>
          <TableCell>{d.file_url ? <a href={d.file_url} className="text-blue-600 underline">Download</a> : "-"}</TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


// =========================================================
// REPORT CENTER
// =========================================================
export function ReportCenterPage() {
  const [reports, setReports] = useState([]);
  const [preview, setPreview] = useState(null);
  useEffect(() => { API.get("/hr-8h/reports").then((r) => setReports(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal")); }, []);
  const load = async (kind) => {
    try { const r = await API.get(`/hr-8h/reports/${kind}`); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="report-center-page">
      <h1 className="text-3xl font-bold">HR Report Center</h1>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Laporan</TableHead><TableHead>Permission</TableHead><TableHead>Aksi</TableHead>
      </TableRow></TableHeader>
      <TableBody>{reports.map((r) => (
        <TableRow key={r.kind} data-testid={`report-row-${r.kind}`}>
          <TableCell><b>{r.kind}</b></TableCell>
          <TableCell className="text-xs">{r.permission}</TableCell>
          <TableCell><Button size="sm" onClick={() => load(r.kind)}>Preview</Button></TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
      {preview && <Card><CardHeader><CardTitle>{preview.kind} — {preview.count} baris</CardTitle></CardHeader>
        <CardContent><pre className="text-xs bg-slate-50 p-3 rounded max-h-96 overflow-auto">{JSON.stringify(preview.rows.slice(0, 20), null, 2)}</pre>
        <p className="italic text-slate-500 text-sm mt-2">{preview.note}</p></CardContent></Card>}
    </div>
  );
}
